import { useFueFlags } from "@/features/feature-tour/use-fue-flags";
import { useCallback, useMemo } from "react";

/**
 * The FUE flag remembering that the user has been welcomed to the Sphere XG Viewer after the
 * migration from HoloBuilder.
 */
const HB_TO_SXG_MIGRATED_FLAG = "sxg:hb-to-sxg-migrated";

type UseWasWelcomedAfterHbToSxgMigration = {
  /** Whether the flags of the current user have been loaded. */
  isLoaded: boolean;

  /** Whether the user has already been welcomed after the migration from HoloBuilder. */
  wasWelcomed: boolean;

  /** Persistently remembers that the current user has been welcomed. */
  markAsWelcomed(): void;
};

/**
 * Tracks whether the user has already been welcomed to the Sphere XG Viewer after the migration
 * from HoloBuilder, so that the welcome is only shown on the first redirect from the WebEditor.
 *
 * @returns Whether the user has already been welcomed, and a callback to remember that they have
 */
export function useWasWelcomedAfterHbToSxgMigration(): UseWasWelcomedAfterHbToSxgMigration {
  const { areFueFlagsLoaded, fueFlags, updateFueFlags } = useFueFlags();

  const markAsWelcomed = useCallback(() => {
    // The flag is not a feature tour topic, so it is kept in the untyped part of the flags
    updateFueFlags((current) => ({
      ...current,
      other: { ...current.other, [HB_TO_SXG_MIGRATED_FLAG]: true },
    }));
  }, [updateFueFlags]);

  return useMemo(
    () => ({
      isLoaded: areFueFlagsLoaded,
      wasWelcomed: fueFlags.other[HB_TO_SXG_MIGRATED_FLAG] === true,
      markAsWelcomed,
    }),
    [areFueFlagsLoaded, fueFlags.other, markAsWelcomed],
  );
}
